import {
  usePostResourceAgendaUploadChecklistImage,
  type ChecklistResponse
} from "@swivu/client-api";
import { CameraIcon } from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { DrawerOrDialog } from "../DrawerOrDialog.js";
import {
  UploadFileDropZone,
  UploadFileOnDropProps
} from "../UploadFileDropZone.js";
import { Button } from "../Button.js";
import { Toast } from "../Toast.js";
import { cn } from "../../utils/cn.js";
import { useIsGummy } from "../../hooks/useGummyTheme.js";
import { Spinner } from "../Spinner.js";

type UploadChecklistImageProps = {
  isOpen: boolean;
  handleClose: () => void;
  item: ChecklistResponse["items"][number];
  queryClient: QueryClient;
  incrementImageCacheBuster: () => void;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const UploadChecklistImageWrapper = ({
  isOpen,
  handleClose,
  item,
  queryClient,
  incrementImageCacheBuster
}: UploadChecklistImageProps) => {
  const isGummy = useIsGummy();
  return (
    <DrawerOrDialog.Root
      drawer={{
        open: isOpen,
        onOpenChange: (isOpen) => {
          if (!isOpen) {
            handleClose();
          }
        }
      }}
      dialog={{
        open: isOpen,
        onOpenChange: (isOpen) => {
          if (!isOpen) {
            handleClose();
          }
        }
      }}
    >
      <DrawerOrDialog.Content
        className={cn(!isGummy && "bg-mono-100/90 dark:bg-mono-950/90")}
      >
        <DrawerOrDialog.Title asChild>
          <div
            className={cn(
              "flex items-start gap-4 text-left",
              isGummy
                ? "pb-4"
                : "dark:border-mono-700/50 dark:text-mono-200 mb-2 border-b px-4 pb-2 pt-6 md:px-0 md:pt-0"
            )}
          >
            <span
              className={cn(
                "flex size-10 shrink-0 items-center justify-center",
                isGummy ? cn("rounded-full text-white", "bg-tile-calendar") : ""
              )}
              style={
                isGummy
                  ? { textShadow: "var(--text-shadow-on-color)" }
                  : undefined
              }
            >
              <CameraIcon
                className={cn(isGummy ? "size-5" : "h-5 w-5")}
                aria-hidden
              />
            </span>
            <div className="min-w-0">
              <h3
                className={cn(
                  isGummy
                    ? "text-[17px] font-black text-mono-900 dark:text-white"
                    : undefined
                )}
              >
                Upload checklist item picture
              </h3>
              <p
                className={cn(
                  "truncate text-sm",
                  isGummy
                    ? "font-extrabold text-mono-500 dark:text-mono-400"
                    : "opacity-50"
                )}
              >
                {item.description}
              </p>
            </div>
          </div>
        </DrawerOrDialog.Title>
        <div
          className={cn(
            "dark:text-mono-200",
            isGummy ? "pt-2" : "px-4 pb-6 pt-4 md:px-0 md:pb-0 md:pt-4"
          )}
        >
          <UploadChecklistImage
            item={item}
            onComplete={handleClose}
            queryClient={queryClient}
            incrementImageCacheBuster={incrementImageCacheBuster}
          />
        </div>
      </DrawerOrDialog.Content>
    </DrawerOrDialog.Root>
  );
};

type UploadChecklistImgProps = {
  onComplete: () => void;
  item: ChecklistResponse["items"][number];
  queryClient: QueryClient;
  incrementImageCacheBuster: () => void;
};

const UploadChecklistImage = ({
  item,
  onComplete,
  queryClient,
  incrementImageCacheBuster
}: UploadChecklistImgProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const onDrop = <T extends File>({
    acceptedFiles
  }: UploadFileOnDropProps<T>) => {
    setFiles([...files, ...acceptedFiles]);
  };
  if (files.length > 0) {
    return (
      <UploadFile
        queryClient={queryClient}
        files={files}
        onCancel={() => setFiles([])}
        onSuccess={() => {
          setFiles([]);
          onComplete();
        }}
        item={item}
        incrementImageCacheBuster={incrementImageCacheBuster}
      />
    );
  }
  return <UploadFileDropZone onDrop={onDrop} />;
};

type UploadFileProps = {
  files: File[];
  onSuccess: () => void;
  onCancel: () => void;
  item: UploadChecklistImgProps["item"];
  queryClient: QueryClient;
  incrementImageCacheBuster: () => void;
};

const FilePreviewRow = ({ file }: { file: File }) => {
  const isGummy = useIsGummy();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!isGummy) {
    return (
      <li className="flex flex-col gap-6 space-y-2 md:flex-row">
        {previewUrl && (
          <img
            src={previewUrl}
            alt={file.name}
            className="max-h-full max-w-full rounded-md"
          />
        )}
        <div className="flex flex-col md:gap-y-6">
          <p className="break-all">{file.name}</p>
          <p>{file.size} bytes</p>
        </div>
      </li>
    );
  }

  return (
    <li
      className={cn(
        "flex flex-col gap-4 rounded-surface-xl p-4 md:flex-row md:items-center md:gap-5 md:p-5",
        "bg-white dark:bg-mono-900"
      )}
    >
      <div
        className={cn(
          "mx-auto flex h-48 w-full items-center justify-center overflow-hidden rounded-surface-md md:mx-0 md:h-40 md:w-40 md:shrink-0",
          "bg-mono-100 dark:bg-mono-800"
        )}
      >
        {previewUrl && (
          <img
            src={previewUrl}
            alt={file.name}
            className="h-full w-full object-cover"
          />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p
          className="break-all text-[15px] font-black text-mono-900 dark:text-white"
          title={file.name}
        >
          {file.name}
        </p>
        <span
          className={cn(
            "inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-widest",
            "bg-white text-mono-600 dark:bg-mono-950 dark:text-mono-300"
          )}
        >
          {formatFileSize(file.size)}
        </span>
      </div>
    </li>
  );
};

const UploadFile = ({
  files,
  onSuccess,
  onCancel,
  item,
  queryClient: _queryClient,
  incrementImageCacheBuster
}: UploadFileProps) => {
  const isGummy = useIsGummy();
  const queryClient = useQueryClient();

  const mutation = usePostResourceAgendaUploadChecklistImage({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["checklist"]
      });
      incrementImageCacheBuster();
      onSuccess();
    },
    onError: (error) => {
      Toast.toast({
        title: "Upload failed",
        description: error.message
      });
    }
  });

  const handleUpload = () => {
    mutation.mutate({
      id: item.id.toString(),
      file: files[0]
    });
  };

  return (
    <div>
      <ul className={cn(isGummy ? "space-y-3" : undefined)}>
        {files.map((file) => (
          <FilePreviewRow key={file.name} file={file} />
        ))}
      </ul>
      {isGummy ? (
        <div
          className={cn(
            "mt-5 flex flex-col-reverse gap-3 md:flex-row md:justify-end md:gap-4",
            "md:sticky md:bottom-0"
          )}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={mutation.isPending}
            className={cn(
              "inline-flex h-12 items-center justify-center rounded-surface-md px-5 text-base font-black",
              "bg-tile-neutral text-mono-800",
              "dark:from-mono-800 dark:to-mono-900 dark:text-mono-100",
              "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
              "motion-reduce:transform-none motion-reduce:transition-none",
              "outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40",
              "disabled:pointer-events-none disabled:opacity-50",
              "w-full md:w-auto md:flex-none"
            )}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={mutation.isPending}
            className={cn(
              "inline-flex h-12 items-center justify-center gap-2 rounded-surface-md px-5 text-base font-black",
              "bg-gradient-to-b from-primary-400 to-primary-600 text-white",
              "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
              "motion-reduce:transform-none motion-reduce:transition-none",
              "outline-none focus-visible:ring-4 focus-visible:ring-white/80",
              "disabled:pointer-events-none disabled:opacity-50 disabled:grayscale",
              mutation.isPending && "cursor-wait",
              "w-full md:w-auto md:flex-none"
            )}
            style={{ textShadow: "var(--text-shadow-on-color)" }}
          >
            {mutation.isPending ? <Spinner className="h-5 w-5" /> : "Upload"}
          </button>
        </div>
      ) : (
        <div
          className={cn(
            "dark:border-mono-700/50 dark:text-mono-200 dark:bg-mono-950/90 mt-4 flex flex-col-reverse gap-4 border-t pt-4 backdrop-blur-sm md:sticky md:bottom-0 md:flex-row md:justify-end"
          )}
        >
          <Button onClick={onCancel} type="button" action="tertiary">
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            action="secondary"
            loading={mutation.isPending}
          >
            Upload
          </Button>
        </div>
      )}
    </div>
  );
};

export default UploadChecklistImageWrapper;
